// adapters/createCustomFieldAdapter.ts
//
// ONE generic FormBuilderAdapter factory. Works identically for
// SessionCustomField, RegistrationCustomField, CaseCustomField — or any other
// entity that follows the same shape (fieldTypeID FK, fieldKey, label,
// category, orderNo, isRequired, options as Base64 JSON, placeholder,
// isActive). You never write a new adapter file per endpoint: you call this
// one factory inline wherever you use <FormBuilder>, passing in whatever
// list/create/update/delete functions your SDK already exposes.
//
//   const adapter = createCustomFieldAdapter({
//     fieldTypes,
//     list:   () => sessionCustomField.list({ courseSessionID }),
//     create: (data) => sessionCustomField.create({ ...data, courseSessionID }),
//     update: (id, data) => sessionCustomField.update(id, data),
//     remove: (id) => sessionCustomField.delete(id),
//   });
//
// Swap `sessionCustomField` for `registrationCustomField` / `caseCustomField`
// and nothing else in this file changes — no new adapter, no new file.

import { BuilderField, FieldOption } from '../types/BuilderField';
import { FormBuilderAdapter } from '../types/BuilderAdapter';

// ---------------------------------------------------------------------------
// Generic, structural record shapes — deliberately NOT imported from any SDK,
// so this file has no hard dependency on '@actschurch/ccms-sdk' and works the
// same whether the record is a SessionCustomField, RegistrationCustomField,
// CaseCustomField, or anything else shaped like this.
// ---------------------------------------------------------------------------

export interface CustomFieldTypeLike {
  typeID: number;
  code: string;
  name: string;
  dataKind: string;
  color?: string;
  icon?: string;
  isActive?: boolean;
}

/**
 * A loaded custom-field record from any endpoint. The primary-key field name
 * varies (sessionCustomFieldID / registrationCustomFieldID / caseCustomFieldID
 * / ...) so it's intentionally left as an open index signature — see
 * `extractId` below for how it's found automatically.
 */
export interface CustomFieldRecord {
  [key: string]: any;
  fieldTypeID: number;
  fieldKey?: string | null;
  label?: string | null;
  category?: string | null;
  orderNo: number;
  isRequired: boolean;
  options?: string | null; // Base64-encoded JSON: FieldOption[]
  placeholder?: string | null;
  isActive: boolean;
}

/** Payload shape sent to your create/update functions — no id, no FK (your bound function adds the FK). */
export interface CustomFieldPayload {
  fieldTypeID: number;
  fieldKey: string;
  label?: string | null;
  category?: string | null;
  orderNo: number;
  isRequired: boolean;
  options: string | null;
  placeholder?: string | null;
  isActive: boolean;
}

// ---------------------------------------------------------------------------
// Base64 <-> FieldOption[] — zero-dependency, works in any environment
// (browser, SSR, RN) without needing Node's Buffer or its type definitions.
// ---------------------------------------------------------------------------

const BASE64_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** UTF-8 safe base64 encode, no Buffer/atob/btoa dependency. */
function base64Encode(input: string): string {
  const bytes = new TextEncoder().encode(input);
  let result = '';
  for (let i = 0; i < bytes.length; i += 3) {
    const b0 = bytes[i];
    const b1 = i + 1 < bytes.length ? bytes[i + 1] : undefined;
    const b2 = i + 2 < bytes.length ? bytes[i + 2] : undefined;

    result += BASE64_CHARS[b0 >> 2];
    result += BASE64_CHARS[((b0 & 0x03) << 4) | ((b1 ?? 0) >> 4)];
    result += b1 !== undefined ? BASE64_CHARS[((b1 & 0x0f) << 2) | ((b2 ?? 0) >> 6)] : '=';
    result += b2 !== undefined ? BASE64_CHARS[b2 & 0x3f] : '=';
  }
  return result;
}

/** UTF-8 safe base64 decode, no Buffer/atob/btoa dependency. */
function base64Decode(input: string): string {
  const clean = input.replace(/[^A-Za-z0-9+/]/g, '');
  const bytes: number[] = [];
  for (let i = 0; i < clean.length; i += 4) {
    const e0 = BASE64_CHARS.indexOf(clean[i]);
    const e1 = BASE64_CHARS.indexOf(clean[i + 1]);
    const e2 = clean[i + 2] !== undefined ? BASE64_CHARS.indexOf(clean[i + 2]) : -1;
    const e3 = clean[i + 3] !== undefined ? BASE64_CHARS.indexOf(clean[i + 3]) : -1;

    bytes.push((e0 << 2) | (e1 >> 4));
    if (e2 >= 0) bytes.push(((e1 & 0x0f) << 4) | (e2 >> 2));
    if (e3 >= 0) bytes.push(((e2 & 0x03) << 6) | e3);
  }
  return new TextDecoder().decode(new Uint8Array(bytes));
}

function encodeOptions(options?: FieldOption[]): string | null {
  if (!options || options.length === 0) return null;
  return base64Encode(JSON.stringify(options));
}

function decodeOptions(raw?: string | null): FieldOption[] {
  if (!raw) return [];
  // Some backends return base64-encoded JSON (the original format this
  // adapter was written for). Others may return a plain JSON string. Try
  // both: first attempt base64 decode + parse, then fall back to parsing
  // the raw string directly. If both fail, return an empty array.
  try {
    const parsed = JSON.parse(base64Decode(raw));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    try {
      const parsed2 = JSON.parse(raw);
      return Array.isArray(parsed2) ? parsed2 : [];
    } catch {
      return [];
    }
  }
}

// ---------------------------------------------------------------------------
// Auto-detect the primary key field, whatever it's called.
// Matches sessionCustomFieldID / registrationCustomFieldID / caseCustomFieldID
// / employeeCustomFieldID / ... falling back to a plain "id" if present.
// ---------------------------------------------------------------------------

const ID_KEY_PATTERN = /CustomFieldID$/i;

function extractId(record: CustomFieldRecord): number {
  const key = Object.keys(record).find((k) => ID_KEY_PATTERN.test(k));
  if (key) return record[key];
  if ('id' in record) return record.id;
  throw new Error(
    `createCustomFieldAdapter: couldn't find a primary key on this record — ` +
      `expected a field ending in "CustomFieldID" (e.g. sessionCustomFieldID) or a plain "id".`
  );
}

// ---------------------------------------------------------------------------
// CustomFieldRecord <-> BuilderField mapping
// ---------------------------------------------------------------------------

export function toBuilderField(record: CustomFieldRecord, typesById: Map<number, CustomFieldTypeLike>): BuilderField {
  const type = typesById.get(record.fieldTypeID);
  return {
    id: extractId(record),
    fieldKey: record.fieldKey ?? '',
    label: record.label ?? '',
    fieldType: type?.code ?? record.fieldTypeCode ?? String(record.fieldTypeID),
    category: record.category ?? 'General',
    orderNo: record.orderNo,
    required: record.isRequired,
    active: record.isActive,
    placeholder: record.placeholder ?? undefined,
    options: decodeOptions(record.options),
  };
}

function fromBuilderField(
  field: BuilderField,
  typesByCode: Map<string, CustomFieldTypeLike>
): CustomFieldPayload {
  const type = typesByCode.get(field.fieldType);
  if (!type) {
    throw new Error(
      `createCustomFieldAdapter: unknown field type code "${field.fieldType}" — ` +
        `make sure it's included in the "fieldTypes" array passed in.`
    );
  }
  return {
    fieldTypeID: type.typeID,
    fieldKey: field.fieldKey,
    label: field.label,
    category: field.category,
    orderNo: field.orderNo,
    isRequired: field.required,
    options: encodeOptions(field.options),
    placeholder: field.placeholder ?? null,
    isActive: field.active,
  };
}

// ---------------------------------------------------------------------------
// The one generic factory
// ---------------------------------------------------------------------------

export interface CreateCustomFieldAdapterConfig {
  /** The full, already-loaded custom field type catalogue (from CustomFieldTypeService.list()). */
  fieldTypes: CustomFieldTypeLike[];
  /**
   * Bind to your SDK's list call, with whatever filter it needs already
   * baked in. Optional — if omitted, loadFields() resolves to an empty
   * array instead of throwing, so a create-only or write-only adapter still
   * type-checks and mounts fine.
   */
  list?: () => Promise<CustomFieldRecord[]>;
  /** Bind to your SDK's create call. Add any foreign key (courseSessionID, registrationID, caseID, ...) inside this function. Optional. */
  create?: (data: CustomFieldPayload) => Promise<CustomFieldRecord>;
  /** Bind to your SDK's update call. Optional. */
  update?: (id: number, data: CustomFieldPayload) => Promise<CustomFieldRecord>;
  /** Bind to your SDK's delete call. Its resolved value (boolean, void, whatever) is ignored. Optional. */
  remove?: (id: number) => Promise<unknown>;
}

/**
 * Domain-agnostic FormBuilderAdapter factory. Works for Session, Registration,
 * Case, or anything else shaped like a "custom field" record — pass in your
 * bound CRUD functions and the field-type catalogue, get back an adapter.
 * No per-endpoint adapter file required.
 *
 * Every CRUD function is optional — supply just the ones your page actually
 * needs (e.g. `create` only for a write-only flow). Calling an operation
 * whose function wasn't provided throws a clear error naming exactly what's
 * missing, rather than failing a type check for unrelated functionality.
 */
export function createCustomFieldAdapter({
  fieldTypes,
  list,
  create,
  update,
  remove,
}: CreateCustomFieldAdapterConfig): FormBuilderAdapter {
  const typesById = new Map(fieldTypes.map((t) => [t.typeID, t]));
  const typesByCode = new Map(fieldTypes.map((t) => [t.code, t]));

  return {
    async loadFields() {
      if (!list) return [];
      const records = await list();
      return [...records]
        .sort((a, b) => a.orderNo - b.orderNo)
        .map((r) => toBuilderField(r, typesById));
    },

    async createField(field) {
      if (!create) {
        throw new Error('createCustomFieldAdapter: no "create" function was provided.');
      }
      const payload = fromBuilderField(field, typesByCode);
      const created = await create(payload);
      return toBuilderField(created, typesById);
    },

    async updateField(field) {
      if (!update) {
        throw new Error('createCustomFieldAdapter: no "update" function was provided.');
      }
      const payload = fromBuilderField(field, typesByCode);
      const updated = await update(Number(field.id), payload);
      return toBuilderField(updated, typesById);
    },

    async deleteField(id) {
      if (!remove) {
        throw new Error('createCustomFieldAdapter: no "remove" function was provided.');
      }
      await remove(Number(id));
    },

    // No bulk-reorder endpoint assumed — persists each affected field's new
    // orderNo individually via `update`. Silently a no-op (order still
    // changes locally) if `update` wasn't provided, since reordering is a
    // convenience, not a required capability.
    async reorderFields(fields) {
      if (!update) return;
      await Promise.all(fields.map((f) => update(Number(f.id), fromBuilderField(f, typesByCode))));
    },
  };
}